// Admin API Client
// Provides methods for all admin CRUD operations

import { authFetch } from '../auth/client';

// ============================================
// DASHBOARD
// ============================================

export async function getDashboardStats() {
  return authFetch('/admin/stats');
}

// ============================================
// POSTS
// ============================================

export async function getPosts(options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.set('page', options.page);
  if (options.limit) params.set('limit', options.limit);
  if (options.status) params.set('status', options.status);
  if (options.category) params.set('category', options.category);
  if (options.author) params.set('author', options.author);
  if (options.search) params.set('search', options.search);

  const query = params.toString();
  return authFetch(`/admin/posts${query ? `?${query}` : ''}`);
}

export async function getPost(id) {
  return authFetch(`/admin/posts?id=${id}`);
}

export async function createPost(data) {
  return authFetch('/admin/posts', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updatePost(id, data) {
  return authFetch(`/admin/posts?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletePost(id) {
  return authFetch(`/admin/posts?id=${id}`, {
    method: 'DELETE'
  });
}

// Quick actions
export async function publishPost(id) {
  return updatePost(id, { status: 'published', publishedAt: new Date().toISOString() });
}

export async function unpublishPost(id) {
  return updatePost(id, { status: 'draft' });
}

export async function archivePost(id) {
  return updatePost(id, { status: 'archived' });
}

export async function featurePost(id, featured = true) {
  return updatePost(id, { isFeatured: featured });
}

// ============================================
// CATEGORIES
// ============================================

export async function getCategories() {
  return authFetch('/admin/categories');
}

export async function getCategory(id) {
  return authFetch(`/admin/categories?id=${id}`);
}

export async function createCategory(data) {
  return authFetch('/admin/categories', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateCategory(id, data) {
  return authFetch(`/admin/categories?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deleteCategory(id) {
  return authFetch(`/admin/categories?id=${id}`, {
    method: 'DELETE'
  });
}

// ============================================
// AUTHORS
// ============================================

export async function getAuthors() {
  return authFetch('/admin/authors');
}

export async function getAuthor(id) {
  return authFetch(`/admin/authors?id=${id}`);
}

export async function createAuthor(data) {
  return authFetch('/admin/authors', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateAuthor(id, data) {
  return authFetch(`/admin/authors?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deleteAuthor(id) {
  return authFetch(`/admin/authors?id=${id}`, {
    method: 'DELETE'
  });
}

// ============================================
// USERS (Admin only)
// ============================================

export async function getUsers() {
  return authFetch('/admin/users');
}

export async function getUser(id) {
  return authFetch(`/admin/users?id=${id}`);
}

export async function createUser(data) {
  return authFetch('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateUser(id, data) {
  return authFetch(`/admin/users?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deleteUser(id) {
  return authFetch(`/admin/users?id=${id}`, {
    method: 'DELETE'
  });
}

// Quick actions
export async function activateUser(id) {
  return updateUser(id, { isActive: true });
}

export async function deactivateUser(id) {
  return updateUser(id, { isActive: false });
}

export async function changeUserRole(id, role) {
  return updateUser(id, { role });
}

export async function resetUserPassword(id, newPassword) {
  return updateUser(id, { password: newPassword });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Generate slug from title
export function generateSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
}

// Calculate read time from content
export function calculateReadTime(content, wordsPerMinute = 200) {
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return `${minutes} min read`;
}

// Extract excerpt from content
export function generateExcerpt(content, maxLength = 160) {
  // Strip HTML tags if present
  const text = content.replace(/<[^>]*>/g, '');
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}
